<script lang="ts">
  /**
   * Somebody next to their name (amendment 21): the picture they uploaded — or their emoji,
   * while there is none or it is still on its way — in the frame they bought.
   *
   * A frame is a ring around the circle, never over it, and it turns or glows by itself. With
   * reduced motion it stands still and keeps its colours: a bought frame is still worn.
   */
  import type { Accessory as Kind, Frame } from '$domain/looks.ts';
  import Accessory from './Accessory.svelte';
  import { looks } from './looks.svelte.ts';

  type Props = {
    username: string;
    emoji: string;
    /** The circle's diameter in px; a frame adds its ring outside it. */
    size?: number;
    /** A frame to show instead of the one worn: the shop's try-on. */
    frame?: Frame | null;
    /** An accessory to show instead of the one worn (amendment 23), likewise. */
    accessory?: Kind | null;
  };
  const { username, emoji, size = 20, frame, accessory }: Props = $props();

  const look = $derived(looks.of(username));
  const picture = $derived(
    look === undefined || look.avatar === null ? undefined : looks.picture(username, look.avatar),
  );
  const worn = $derived(frame === undefined ? (look?.frame ?? null) : frame);
  const dressed = $derived(accessory === undefined ? (look?.accessory ?? null) : accessory);
</script>

<span
  class="avatar"
  class:framed={worn !== null}
  data-frame={worn}
  style:--size="{size}px"
  aria-hidden="true"
>
  {#if picture !== undefined}
    <img src={picture} alt="" draggable="false" />
  {:else}
    <span class="emoji">{emoji}</span>
  {/if}
  {#if dressed !== null}<Accessory kind={dressed} />{/if}
</span>

<style>
  .avatar {
    --ring: max(2px, calc(var(--size) * 0.1));
    position: relative;
    display: inline-grid;
    flex: none;
    place-items: center;
    width: var(--size);
    height: var(--size);
    /* Room for a ring whether one is worn or not, so names in a list start in one column. */
    margin: var(--ring);
    border-radius: 50%;
    line-height: 1;
    vertical-align: middle;
  }

  img {
    width: 100%;
    height: 100%;
    border-radius: 50%;
    object-fit: cover;
  }

  .emoji {
    font-size: calc(var(--size) * 0.8);
  }

  .framed::before,
  .framed::after {
    content: '';
    position: absolute;
    inset: calc(var(--ring) * -1);
    border-radius: 50%;
    pointer-events: none;
  }

  /* The ring: a disc with the circle cut out of it, so nothing is painted over the face. */
  .framed::before {
    -webkit-mask: radial-gradient(
      closest-side,
      transparent calc(100% - var(--ring)),
      #000 calc(100% - var(--ring) + 0.5px)
    );
    mask: radial-gradient(
      closest-side,
      transparent calc(100% - var(--ring)),
      #000 calc(100% - var(--ring) + 0.5px)
    );
    animation: spin var(--turn, 4s) linear infinite;
  }

  /* The glow: a shadow outside the ring, breathing. */
  .framed::after {
    animation: glow 1.8s ease-in-out infinite alternate;
  }

  [data-frame='neon']::before {
    --turn: 5s;
    background: conic-gradient(#ff2bd6, #22e4ff, #ff2bd6);
  }

  [data-frame='neon']::after {
    box-shadow:
      0 0 calc(var(--ring) * 1.5) #ff2bd6,
      0 0 calc(var(--ring) * 3) #22e4ff;
  }

  [data-frame='rainbow']::before {
    --turn: 3s;
    background: conic-gradient(
      #ff3b30,
      #ff9500,
      #ffcc00,
      #34c759,
      #00c7be,
      #007aff,
      #af52de,
      #ff3b30
    );
  }

  [data-frame='fire']::before {
    --turn: 1.6s;
    background: conic-gradient(
      #ff3d00,
      #ffb300,
      #ff6d00,
      #ffe082,
      #ff3d00,
      #ff9100,
      #d50000,
      #ff3d00
    );
  }

  [data-frame='fire']::after {
    box-shadow: 0 0 calc(var(--ring) * 2) #ff6d00;
    animation: flicker 0.9s ease-in-out infinite;
  }

  /* Cells of comb, divided by darker wax. */
  [data-frame='honey']::before {
    --turn: 9s;
    background: repeating-conic-gradient(#ffc93c 0 26deg, #a86400 0 30deg);
  }

  [data-frame='honey']::after {
    box-shadow: 0 0 calc(var(--ring) * 1.5) rgb(255 180 0 / 70%);
  }

  /* A mirror ball: it jumps from facet to facet rather than turning smoothly. */
  [data-frame='disco']::before {
    --turn: 2.4s;
    background: repeating-conic-gradient(
      #ff4ecd 0 22.5deg,
      #4ef0ff 0 45deg,
      #fff35c 0 67.5deg,
      #7cff4e 0 90deg
    );
    animation-timing-function: steps(8);
  }

  [data-frame='disco']::after {
    box-shadow:
      0 0 calc(var(--ring) * 1.5) #fff,
      0 0 calc(var(--ring) * 3) #ff4ecd;
    animation: blink 0.8s step-end infinite;
  }

  [data-frame='gold']::before {
    --turn: 6s;
    background: conic-gradient(#7a5a00, #ffe27a, #c99a1e, #fff7d1, #a87b00, #ffd75e, #7a5a00);
  }

  [data-frame='gold']::after {
    box-shadow: 0 0 calc(var(--ring) * 2) rgb(255 205 60 / 80%);
  }

  @keyframes spin {
    to {
      transform: rotate(1turn);
    }
  }

  @keyframes glow {
    from {
      opacity: 0.35;
    }
    to {
      opacity: 1;
    }
  }

  @keyframes flicker {
    0%,
    100% {
      opacity: 0.75;
    }
    25% {
      opacity: 1;
    }
    50% {
      opacity: 0.5;
    }
    75% {
      opacity: 0.95;
    }
  }

  @keyframes blink {
    0% {
      opacity: 1;
    }
    50% {
      opacity: 0.25;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .framed::before,
    .framed::after {
      animation: none;
    }
  }
</style>
